import { useEffect, useMemo, useRef, useState } from 'react';
import { setAccountFactRemoved, useAccountKnowledge, type AccountFact } from '@/lib/account-knowledge';
import { useTeam } from '@/lib/work-handoffs';
import { cn } from '@/lib/utils';
import { AccountFactRow } from './AccountFactRow';
import { AccountDossierText } from './AccountDossierText';
import { AccountPeople } from './AccountPeople';
import type { CrmContact } from '@/lib/crm-account';

/** How long « Annuler » stays after a removal. */
const UNDO_MS = 6_000;

/**
 * « Ce qu'on sait » (26/10/2026): what the reader learned about the account,
 * from runs, tasks, the page's chat and e-mails. What arrived since the last
 * visit comes first; the short text follows, each sentence pointing to its
 * facts; every fact goes away in one click, with an undo. Nothing shows while
 * nothing is known (« Rien de vide ne prend de place »).
 */
export const AccountKnowledge = ({ accountSubjectId, contacts = [] }: {
  accountSubjectId: string;
  /** HubSpot contacts of the account, shown in « Personnes » with the people the facts name. */
  contacts?: CrmContact[];
}) => {
  const { knowledge, error, reload, setKnowledge } = useAccountKnowledge(accountSubjectId);
  const team = useTeam();
  const [undo, setUndo] = useState<{ fact: AccountFact; failed?: string } | null>(null);
  const undoTimer = useRef<number | null>(null);
  // A restore waits for the removal it undoes: the two must reach the server in order.
  const pending = useRef(new Map<string, Promise<void>>());
  const nameOf = (userId: string) => team?.members.find((member) => member.userId === userId)?.name ?? null;

  useEffect(() => () => { if (undoTimer.current !== null) window.clearTimeout(undoTimer.current); }, []);

  const facts = knowledge?.facts ?? [];
  const active = useMemo(() => facts.filter((fact) => fact.state === 'active'), [facts]);
  const byId = useMemo(() => new Map(facts.map((fact) => [fact.id, fact])), [facts]);
  // Arrived since the last visit (live ones too). The first visit shows the
  // text, not every fact as « new ».
  const shownArrivals = useMemo(() => {
    const since = knowledge?.lastSeenAt;
    return since ? active.filter((fact) => fact.createdAt > since).sort((left, right) => left.createdAt.localeCompare(right.createdAt)) : [];
  }, [active, knowledge?.lastSeenAt]);

  const setState = (factId: string, state: AccountFact['state']) =>
    setKnowledge((current) => current && ({ ...current, facts: current.facts.map((fact) => fact.id === factId ? { ...fact, state } : fact) }));

  const remove = (fact: AccountFact) => {
    setState(fact.id, 'removed');
    if (undoTimer.current !== null) window.clearTimeout(undoTimer.current);
    setUndo({ fact });
    undoTimer.current = window.setTimeout(() => setUndo(null), UNDO_MS);
    const removal = setAccountFactRemoved(fact.id, true).catch((failure) => {
      setState(fact.id, 'active');
      setUndo({ fact, failed: failure instanceof Error ? failure.message : 'Le fait n’a pas pu être retiré.' });
      throw failure;
    });
    pending.current.set(fact.id, removal.catch(() => undefined));
  };

  const restore = (fact: AccountFact) => {
    if (undoTimer.current !== null) window.clearTimeout(undoTimer.current);
    setUndo(null);
    setState(fact.id, 'active');
    void (pending.current.get(fact.id) ?? Promise.resolve())
      .then(() => setAccountFactRemoved(fact.id, false))
      .catch(() => {
        setState(fact.id, 'removed');
        void reload();
      });
  };

  if (error) {
    return <p role="alert" className="mb-8 text-sm text-domino-inkSoft">{error} <button type="button" onClick={() => void reload()} className="text-domino-aubergine underline">Réessayer</button></p>;
  }
  const people = knowledge
    ? <AccountPeople contacts={contacts} facts={facts} accountSubjectId={accountSubjectId} nameOf={nameOf} onRemove={remove} />
    : null;
  if (!knowledge || (active.length === 0 && !undo)) return people;

  const row = (fact: AccountFact, extra: { fresh?: boolean; index?: number } = {}) => (
    <AccountFactRow key={fact.id} fact={fact} accountSubjectId={accountSubjectId} nameOf={nameOf} onRemove={remove}
      before={fact.replacesFactId ? byId.get(fact.replacesFactId) ?? null : null} {...extra} />
  );

  return (
    <>
    <section className="mb-8" aria-labelledby="account-knowledge-title">
      <h2 id="account-knowledge-title" className="mb-3 text-lg font-semibold text-domino-ink">Ce qu’on sait</h2>
      <div className="overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white">
        {shownArrivals.length > 0 && (
          <div className="border-b border-domino-cardBorder bg-domino-lilac/40">
            <p className="px-5 pt-3 text-xs font-semibold text-domino-ink sm:px-6">Nouveau</p>
            <ul className="divide-y divide-domino-cardBorder/70">{shownArrivals.map((fact, index) => row(fact, { fresh: true, index }))}</ul>
          </div>
        )}
        {knowledge.dossier && knowledge.dossier.length > 0 ? (
          <AccountDossierText dossier={knowledge.dossier} facts={byId} accountSubjectId={accountSubjectId} nameOf={nameOf} onRemove={remove} />
        ) : (
          <ul className="divide-y divide-domino-cardBorder">{active.filter((fact) => !shownArrivals.includes(fact)).map((fact) => row(fact))}</ul>
        )}
        {knowledge.dossier && knowledge.dossier.length > 0 && active.length > 0 && (
          <details className="group border-t border-domino-cardBorder">
            <summary className="cursor-pointer list-none px-5 py-3 text-xs font-medium text-domino-inkSoft hover:text-domino-aubergine focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune sm:px-6">
              Tous les faits
            </summary>
            <ul className="divide-y divide-domino-cardBorder border-t border-domino-cardBorder">{active.map((fact) => row(fact))}</ul>
          </details>
        )}
      </div>
      {undo && (
        <div role="status" className={cn(
          'fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl bg-domino-ink px-4 py-2.5 text-sm text-domino-warm shadow-[var(--domino-shadow-elevated)] motion-safe:animate-slideIn',
        )}>
          <span>{undo.failed ?? 'Fait retiré'}</span>
          {!undo.failed && (
            <button type="button" onClick={() => restore(undo.fact)} className="font-semibold text-domino-sun underline-offset-2 hover:underline active:scale-[0.97]">
              Annuler
            </button>
          )}
        </div>
      )}
    </section>
    {people}
    </>
  );
};
